import React, { useCallback, useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { Project } from '../types';
import { ContentService } from '../services/content';
const ProjectDetail: React.FC = () => {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'video' | 'gallery'>('gallery');
  const [inlineGalleryIndex, setInlineGalleryIndex] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const assetContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const load = async () => {
      if (!slug) return;
      const data = await ContentService.getProjectBySlug(slug);
      if (!data) {
        navigate('/projects');
        return;
      }
      setProject(data);
      setLoading(false);
      if (data.videoUrl && data.videoUrl.trim() !== '') {
        setActiveTab('video');
      } else {
        setActiveTab('gallery');
      }
    };
    load();
  }, [slug, navigate]);
  const toggleFullscreen = () => {
    if (!assetContainerRef.current) return;
    if (!document.fullscreenElement) {
      assetContainerRef.current.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable full-screen mode: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };
  const nextInline = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (project?.gallery && project.gallery.length > 0) {
      setInlineGalleryIndex((inlineGalleryIndex + 1) % project.gallery.length);
    }
  };
  const prevInline = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (project?.gallery && project.gallery.length > 0) {
      setInlineGalleryIndex((inlineGalleryIndex - 1 + project.gallery.length) % project.gallery.length);
    }
  };
  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const nextLightbox = useCallback(() => {
    if (project?.gallery && lightboxIndex !== null) {
      setLightboxIndex((lightboxIndex + 1) % project.gallery.length);
    }
  }, [lightboxIndex, project?.gallery]);
  const prevLightbox = useCallback(() => {
    if (project?.gallery && lightboxIndex !== null) {
      setLightboxIndex((lightboxIndex - 1 + project.gallery.length) % project.gallery.length);
    }
  }, [lightboxIndex, project?.gallery]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (lightboxIndex !== null) {
        if (e.key === 'ArrowRight') nextLightbox();
        if (e.key === 'ArrowLeft') prevLightbox();
        if (e.key === 'Escape') closeLightbox();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, nextLightbox, prevLightbox]);
  const StructuredContent = ({
    text
  }: {
    text: string;
  }) => {
    if (!text) return null;
    const lines = text.split('\n').filter(t => t.trim().length > 0);
    const contentElements: React.ReactNode[] = [];
    let currentList: string[] = [];
    const flushList = () => {
      if (currentList.length > 0) {
        contentElements.push(<ul key={`list-${contentElements.length}`} className="space-y-4 ml-6 list-disc" data-id="element-256">
            {currentList.map((item, idx) => <li key={idx} className="text-xl md:text-2xl text-zinc-400 font-normal tracking-tight pl-2" data-id="element-257">
                {item.replace(/^[•*]\s*/, '')}
              </li>)}
          </ul>);
        currentList = [];
      }
    };
    lines.forEach((line, i) => {
      const trimmed = line.trim();
      const isBullet = trimmed.startsWith('*') || trimmed.startsWith('•');
      if (isBullet) {
        currentList.push(trimmed);
      } else {
        flushList();
        const separator = trimmed.includes(':') ? ':' : trimmed.includes('—') ? '—' : null;
        if (separator) {
          const parts = trimmed.split(separator);
          contentElements.push(<div key={i} className="group" data-id="element-258">
              <h4 className="text-2xl md:text-3xl font-bold text-white mb-2 tracking-tight" data-id="element-259">
                {parts[0].trim()}
              </h4>
              <p className="text-lg md:text-xl text-zinc-400 leading-relaxed max-w-5xl font-medium" data-id="element-260">
                {parts.slice(1).join(separator).trim()}
              </p>
            </div>);
        } else {
          contentElements.push(<p key={i} className="text-xl md:text-2xl text-zinc-400 leading-relaxed font-normal max-w-5xl tracking-tight" data-id="element-261">
              {trimmed}
            </p>);
        }
      }
    });
    flushList();
    return <div className="space-y-6" data-id="element-262">{contentElements}</div>;
  };
  const Section = ({
    title,
    content,
    label
  }: {
    title: string;
    content?: string;
    label: string;
  }) => {
    if (!content || content.trim() === '') return null;
    return <div className="mb-10 md:mb-[72px] last:mb-0" data-id="element-263">
        <div className="flex items-center space-x-6 mb-6" data-id="element-264">
          <span className="text-[10px] text-zinc-500 font-black uppercase tracking-[0.4em] whitespace-nowrap" data-id="element-265">
            {label}
          </span>
          <div className="h-px flex-grow bg-white/10" data-id="element-266"></div>
        </div>
        <h3 className="text-3xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter mb-6 lowercase leading-[0.85]" data-id="element-267">
          {title}
        </h3>
        <StructuredContent text={content} data-id="element-268" />
      </div>;
  };
  const FlowSection = ({
    flow
  }: {
    flow?: string;
  }) => {
    if (!flow || flow.trim() === '') return null;
    const steps = flow.split('→').map(s => s.trim()).filter(Boolean);
    return <div className="mb-10 md:mb-[72px] last:mb-0" data-id="element-269">
        <div className="flex items-center space-x-6 mb-6" data-id="element-270">
          <span className="text-[10px] text-zinc-500 font-black uppercase tracking-[0.4em] whitespace-nowrap" data-id="element-271">
            .workflow
          </span>
          <div className="h-px flex-grow bg-white/10" data-id="element-272"></div>
        </div>
        <h3 className="text-3xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter mb-10 md:mb-16 lowercase leading-[0.85]" data-id="element-273">
          the process
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-10 md:gap-y-14" data-id="element-274">
          {steps.map((step, i) => <div key={i} className="flex items-start space-x-5" data-id="element-275">
              <span className="text-xl font-extrabold text-zinc-700 tabular-nums mt-0.5 shrink-0" data-id="element-276">
                {String(i + 1).padStart(2, '0')}
              </span>
              <div data-id="element-277">
                <h4 className="text-xl md:text-2xl font-extrabold text-white uppercase tracking-tight leading-tight mb-3" data-id="element-278">
                  {step}
                </h4>
                <div className="w-8 h-[2px] bg-white/30" data-id="element-279"></div>
              </div>
            </div>)}
        </div>
      </div>;
  };
  if (loading || !project) return <div className="min-h-screen bg-black flex items-center justify-center" data-id="element-280">
        <div className="flex flex-col items-center space-y-6" data-id="element-281">
          <div className="w-10 h-10 border-2 border-white/5 border-t-white rounded-full animate-spin" data-id="element-282"></div>
          <span className="text-[10px] uppercase tracking-[0.4em] text-zinc-600 font-extrabold" data-id="element-283">
            ACCESSING BUILD ENGINES
          </span>
        </div>
      </div>;
  return <div className="pb-24 pt-[104px] md:pt-32 bg-black selection:bg-white selection:text-black" data-id="element-284">
      {/* Video/Gallery switcher rendered via portal to escape z-[50] stacking context on <main> */}
      {createPortal(<div className="fixed bottom-10 left-1/2 -translate-x-1/2 z-[500] flex items-center p-1.5 bg-zinc-900/90 backdrop-blur-2xl border border-white/10 rounded-full shadow-2xl" data-id="element-285">
          <button onClick={() => setActiveTab('video')} disabled={!project.videoUrl} className={`px-6 py-2.5 rounded-full text-[10px] font-extrabold uppercase tracking-widest transition-all cursor-target flex items-center gap-2 ${activeTab === 'video' ? 'bg-white text-black' : 'text-zinc-400 hover:text-white disabled:opacity-20 disabled:grayscale'}`} data-id="element-286">
            <span data-id="element-287">Video</span>
            {!project.videoUrl && <span className="text-[8px] opacity-40 font-bold" data-id="element-288">N/A</span>}
          </button>
          <div className="w-px h-4 bg-white/10 mx-1" data-id="element-289"></div>
          <button onClick={() => setActiveTab('gallery')} className={`px-6 py-2.5 rounded-full text-[10px] font-extrabold uppercase tracking-widest transition-all cursor-target ${activeTab === 'gallery' ? 'bg-white text-black' : 'text-zinc-400 hover:text-white'}`} data-id="element-290">
            Gallery
          </button>
        </div>, document.body)}

      <div className="max-w-screen-2xl mx-auto px-6 md:px-12" data-id="element-291">
        {/* Relative z-50 to stay on top of the blur overlay */}
        <button onClick={() => navigate('/projects')} className="relative z-[50] flex items-center space-x-4 text-zinc-600 hover:text-white transition-colors mb-6 md:mb-16 cursor-target group" data-id="element-292">
          <span className="text-[10px] uppercase tracking-[0.4em] font-extrabold" data-id="element-293">
            ← back to all builds
          </span>
        </button>

        <header className="mb-10 md:mb-32 space-y-6" data-id="element-294">
          <div className="flex flex-wrap gap-3 md:gap-4" data-id="element-295">
            {['build_unit', project.category, project.year].map((tag, i) => <span key={i} className="bg-white/5 border border-white/10 px-6 py-2 rounded-full text-[9px] font-extrabold uppercase tracking-[0.3em] text-zinc-500" data-id="element-296">
                {tag === 'build_unit' ? `project_${project.id}` : tag}
              </span>)}
          </div>
          <h1 className="text-5xl md:text-9xl font-extrabold tracking-tighter lowercase leading-[0.95]" data-id="element-297">
            {project.title}
          </h1>
          <p className="text-xl md:text-3xl text-zinc-400 font-medium max-w-5xl leading-snug tracking-tight" data-id="element-298">
            {project.description}
          </p>
        </header>

        <section ref={assetContainerRef} className="mb-10 md:mb-24 shadow-2xl rounded-sm overflow-hidden bg-zinc-900 border border-white/5 relative aspect-video group/container" data-id="element-299">
          <button onClick={toggleFullscreen} className="absolute top-6 right-6 z-20 p-3 bg-black/40 backdrop-blur-xl border border-white/10 rounded-full opacity-0 group-hover/container:opacity-100 transition-opacity hover:bg-white hover:text-black cursor-target" title="Toggle Fullscreen" data-id="element-300">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" data-id="element-301">
              <path d="M1.5 1a.5.5 0 0 0-.5.5v4a.5.5 0 0 1-1 0v-4A1.5 1.5 0 0 1 1.5 0h4a.5.5 0 0 1 0 1h-4zM10 .5a.5.5 0 0 1 .5-.5h4A1.5 1.5 0 0 1 16 1.5v4a.5.5 0 0 1-1 0v-4a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 1-.5-.5zM.5 10a.5.5 0 0 1 .5.5v4a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 0 14.5v-4a.5.5 0 0 1 .5-.5zm15 0.5a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 0 0 1h4a.5.5 0 0 1. .5.5v4a.5.5 0 0 1-1 0v-4a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 1 0-1h4A1.5 1.5 0 0 1 16 14.5v-4z" data-id="element-302" />
            </svg>
          </button>

          {activeTab === 'video' && project.videoUrl ? <video src={project.videoUrl} autoPlay loop muted playsInline controls className="w-full h-full object-cover" data-id="element-303" /> : <div className="w-full h-full relative group/gallery cursor-target" onClick={() => (project.gallery?.length || 0) > 0 && openLightbox(inlineGalleryIndex)} data-id="element-304">
              <img src={project.gallery && project.gallery.length > 0 ? project.gallery[inlineGalleryIndex] : project.coverImage} className="w-full h-full object-cover grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-700" alt={project.title} data-id="element-305" />
              {activeTab === 'gallery' && (project.gallery?.length || 0) > 1 && <>
                    <button onClick={e => {
              e.stopPropagation();
              prevInline(e);
            }} className="absolute left-6 top-1/2 -translate-y-1/2 p-4 bg-black/40 backdrop-blur-md rounded-full border border-white/10 hover:bg-white hover:text-black transition-all" data-id="element-306">
                      ←
                    </button>
                    <button onClick={e => {
              e.stopPropagation();
              nextInline(e);
            }} className="absolute right-6 top-1/2 -translate-y-1/2 p-4 bg-black/40 backdrop-blur-md rounded-full border border-white/10 hover:bg-white hover:text-black transition-all" data-id="element-307">
                      →
                    </button>
                  </>}
            </div>}
        </section>

        <article className="max-w-7xl mx-auto space-y-10 md:space-y-24" data-id="element-308">
          <Section label="01. the problem" title="identifying friction" content={project.problem} data-id="element-309" />
          <Section label="02. the solution" title="architecting the fix" content={project.solution} data-id="element-310" />
          <Section label="03. tech & tools" title="system backbone" content={project.tech_stack} data-id="element-311" />
          <Section label="04. key features" title="core product logic" content={project.features} data-id="element-312" />
          <Section label="05. outcomes" title="impact & perspective" content={project.outcomes} data-id="element-313" />
          <FlowSection flow={project.flow} data-id="element-314" />
        </article>
      </div>

      {lightboxIndex !== null && project?.gallery && <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center animate-in fade-in duration-300" onClick={closeLightbox} data-id="element-315">
          <button onClick={closeLightbox} className="absolute top-10 right-10 text-zinc-500 hover:text-white cursor-target uppercase text-[10px] font-bold tracking-[0.4em] z-[120]" data-id="element-316">
            close [esc]
          </button>

          <img src={project.gallery[lightboxIndex]} className="max-w-[90vw] max-h-[75vh] object-contain animate-in zoom-in-95 duration-500 shadow-2xl" alt="Gallery item" onClick={e => e.stopPropagation()} data-id="element-317" />

          {/* Navigation — below image on mobile, sides on desktop */}
          <div className="absolute bottom-16 left-0 right-0 flex items-center justify-between px-6 md:hidden z-[110]" data-id="element-318">
            <button onClick={e => {
          e.stopPropagation();
          prevLightbox();
        }} className="p-4 bg-white/10 backdrop-blur-md rounded-full border border-white/20 hover:bg-white hover:text-black transition-all cursor-target" data-id="element-319">
              <span className="text-2xl font-light" data-id="element-320">←</span>
            </button>
            <span className="text-zinc-600 text-[10px] font-bold uppercase tracking-[0.5em]" data-id="element-321">
              {lightboxIndex + 1} / {project.gallery.length}
            </span>
            <button onClick={e => {
          e.stopPropagation();
          nextLightbox();
        }} className="p-4 bg-white/10 backdrop-blur-md rounded-full border border-white/20 hover:bg-white hover:text-black transition-all cursor-target" data-id="element-322">
              <span className="text-2xl font-light" data-id="element-323">→</span>
            </button>
          </div>

          {/* Desktop side arrows */}
          <button onClick={e => {
        e.stopPropagation();
        prevLightbox();
      }} className="hidden md:flex absolute left-12 top-1/2 -translate-y-1/2 z-[110] text-white/30 hover:text-white transition-all p-10 hover:bg-white/5 rounded-full cursor-target group" data-id="element-324">
            <span className="text-6xl font-light transition-transform group-hover:-translate-x-1" data-id="element-325">
              ←
            </span>
          </button>
          <button onClick={e => {
        e.stopPropagation();
        nextLightbox();
      }} className="hidden md:flex absolute right-12 top-1/2 -translate-y-1/2 z-[110] text-white/30 hover:text-white transition-all p-10 hover:bg-white/5 rounded-full cursor-target group" data-id="element-326">
            <span className="text-6xl font-light transition-transform group-hover:translate-x-1" data-id="element-327">
              →
            </span>
          </button>

          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 text-zinc-600 text-[10px] font-bold uppercase tracking-[0.5em] pointer-events-none hidden md:block" data-id="element-328">
            Archive {lightboxIndex + 1} / {project.gallery.length}
          </div>
        </div>}
    </div>;
};
export default ProjectDetail;